import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchRoomTypes,
  fetchHotelConfigurations,
  createHotelConfiguration,
  updateHotelConfiguration,
  deleteHotelConfiguration,
  createRoomType,
  uploadRoomImage,
  deleteRoomImage,
} from "../../services/hotelConfigurations";
import { toast } from "react-toastify";

const processRoomImages = async (configId, roomFiles = [], previousUrls = []) => {
  const finalUrls = [];
  for (let i = 0; i < 3; i++) {
    const fileOrString = roomFiles[i];
    const slot = `room_${i}`;

    if (fileOrString instanceof File) {
      const publicUrl = await uploadRoomImage(configId, fileOrString, slot);
      if (publicUrl) {
        finalUrls.push(publicUrl);
      } else if (previousUrls[i]) {
        finalUrls.push(previousUrls[i]);
      }
    } else if (typeof fileOrString === 'string' && fileOrString.trim() !== '') {
      finalUrls.push(fileOrString);
    } else {
      await deleteRoomImage(configId, slot);
    }
  }
  return finalUrls;
};

export const getRoomTypes = createAsyncThunk(
  "hotelConfigurations/getRoomTypes",
  async (_, thunkAPI) => {
    try {
      const { data, error } = await fetchRoomTypes();
      if (error) throw error;
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const getHotelConfigurations = createAsyncThunk(
  "hotelConfigurations/getHotelConfigurations",
  async (hotelId, thunkAPI) => {
    try {
      const { data, error } = await fetchHotelConfigurations(hotelId);
      if (error) throw error;
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const addHotelConfiguration = createAsyncThunk(
  "hotelConfigurations/addHotelConfiguration",
  async ({ hotel_id, room_type_id, is_non_refundable, meal_plan, notes, custom_name, roomFiles, additional_info }, thunkAPI) => {
    try {
      const result = await toast.promise(
        createHotelConfiguration({
          hotel_id,
          room_type_id,
          is_non_refundable,
          meal_plan,
          notes: notes || null,
          custom_name: custom_name || null,
          additional_info: additional_info || {},
        }).then(async (res) => {
          if (res.error) throw res.error;
          const createdConfig = res.data[0];

          if (roomFiles && roomFiles.some(f => f !== null)) {
            const imageUrls = await processRoomImages(createdConfig.id, roomFiles, []);
            if (imageUrls.length > 0) {
              const updatedRes = await updateHotelConfiguration(createdConfig.id, { room_image_urls: imageUrls });
              if (updatedRes.data && updatedRes.data[0]) {
                return updatedRes;
              }
            }
          }
          return res;
        }),
        {
          pending: "Creando configuración...",
          success: "Configuración creada exitosamente",
          error: {
            render({ data }) {
              const msg = data?.message || '';
              if (msg.includes('duplicate key') || msg.includes('hotel_configurations_hotel_id_rt_nr_meal_plan_key')) {
                return 'Ya existe una configuración con esta misma habitación, régimen y reembolsabilidad.';
              }
              return `Error: ${msg || 'Error al crear la configuración'}`;
            }
          }
        }
      );

      return result.data[0];
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const editHotelConfiguration = createAsyncThunk(
  "hotelConfigurations/editHotelConfiguration",
  async ({ id, room_type_id, is_non_refundable, meal_plan, notes, custom_name, roomFiles, previousUrls, additional_info }, thunkAPI) => {
    try {
      const result = await toast.promise(
        (async () => {
          let updatedUrls = previousUrls || [];
          if (roomFiles) {
            updatedUrls = await processRoomImages(id, roomFiles, previousUrls || []);
          }

          const res = await updateHotelConfiguration(id, {
            room_type_id,
            is_non_refundable,
            meal_plan,
            notes: notes || null,
            custom_name: custom_name || null,
            room_image_urls: updatedUrls,
            additional_info: additional_info || {},
          });
          if (res.error) throw res.error;
          return res;
        })(),
        {
          pending: "Actualizando configuración...",
          success: "Configuración actualizada exitosamente",
          error: {
            render({ data }) {
              const msg = data?.message || '';
              if (msg.includes('duplicate key') || msg.includes('hotel_configurations_hotel_id_rt_nr_meal_plan_key')) {
                return 'Ya existe otra configuración con esta misma habitación, régimen y reembolsabilidad.';
              }
              return `Error: ${msg || 'Error al actualizar la configuración'}`;
            }
          }
        }
      );

      return result.data[0];
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const removeHotelConfiguration = createAsyncThunk(
  "hotelConfigurations/removeHotelConfiguration",
  async (id, thunkAPI) => {
    try {
      await toast.promise(
        deleteHotelConfiguration(id).then((res) => {
          if (res.error) throw res.error;
          return res;
        }),
        {
          pending: "Eliminando configuración...",
          success: "Configuración eliminada",
          error: {
            render({ data }) {
              return `Error: ${data?.message || 'Error al eliminar'}`;
            }
          }
        }
      );
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const addRoomType = createAsyncThunk(
  "hotelConfigurations/addRoomType",
  async ({ name, description }, thunkAPI) => {
    try {
      const result = await toast.promise(
        createRoomType(name, description).then((res) => {
          if (res.error) throw res.error;
          return res;
        }),
        {
          pending: "Creando tipo de habitación...",
          success: "Tipo de habitación creado",
          error: {
            render({ data }) {
              return `Error: ${data?.message || 'Error al crear tipo de habitación'}`;
            }
          }
        }
      );

      return result.data[0];
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);
